import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clientPortalService, setClientToken, setCachedClient } from '../services/clientPortalService';

interface InviteInfo {
  email: string;
  name: string | null;
  companyName: string | null;
  companyLogoUrl: string | null;
}

export default function ClientSetup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    const e = params.get('email');
    const t = params.get('token');
    if (e) setEmail(e);
    if (t) {
      setToken(t);
      setResolving(true);
      clientPortalService
        .resolveInvite(t)
        .then((data) => {
          setInvite(data);
          if (data?.email) setEmail(data.email);
        })
        .catch(() => {
          // silent — page still works with query params
        })
        .finally(() => setResolving(false));
    }
  }, [params]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const res = await clientPortalService.acceptInvite(email.trim(), token, password);
      setClientToken(res.access_token);
      setCachedClient(res.client);
      navigate('/client/dashboard', { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to set up your account. The invite may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const companyName = invite?.companyName || 'Your contractor';
  const logoUrl = invite?.companyLogoUrl || null;

  return (
    <div className="min-h-screen bg-[#F8F8F7] flex flex-col">
      <header className="w-full border-b border-[#E6E6E4] bg-white">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={companyName}
              className="h-8 w-auto max-w-[140px] object-contain"
            />
          ) : (
            <div className="h-8 w-8 rounded-md bg-orange-500 text-white flex items-center justify-center font-semibold text-sm">
              {companyName.slice(0, 2).toUpperCase()}
            </div>
          )}
          <div className="text-sm font-semibold text-[#181818]">{companyName}</div>
          <div className="ml-auto text-xs text-[#686E6C] hidden sm:block">
            Client portal setup
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-start sm:items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm border border-[#E6E6E4] overflow-hidden">
            <div className="px-6 py-5 border-b border-[#E6E6E4] bg-gradient-to-br from-orange-50/70 to-white">
              <div className="text-xs uppercase tracking-wide font-semibold text-orange-600">
                Welcome
              </div>
              <h1 className="text-xl font-semibold text-[#181818] mt-1">
                {invite?.name ? `Hi ${invite.name.split(' ')[0]},` : 'Set up your portal'}
              </h1>
              <p className="text-sm text-[#686E6C] mt-1.5">
                Choose a password to access your quotes, jobs, certificates and invoices from{' '}
                <span className="font-medium text-[#181818]">{companyName}</span>.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#181818] mb-1.5">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={!!params.get('email') || !!invite?.email}
                  className="w-full h-10 px-3 rounded-lg border border-[#E6E6E4] bg-white text-sm text-[#181818] disabled:bg-[#F8F8F7] focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#181818] mb-1.5">New password</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full h-10 px-3 rounded-lg border border-[#E6E6E4] bg-white text-sm text-[#181818] focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#181818] mb-1.5">Confirm password</label>
                <input
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-[#E6E6E4] bg-white text-sm text-[#181818] focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              {!token && !resolving && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Your invite link is missing a token. Please use the link from your email.
                </div>
              )}
              {error && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading || !token}
                className="w-full h-11 rounded-lg bg-orange-500 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50 shadow-sm"
              >
                {loading ? 'Activating…' : 'Activate my account'}
              </button>
            </form>
          </div>

          <p className="text-xs text-[#9CA3AF] text-center mt-4">
            Powered by Certs • Your data is kept private to {companyName}.
          </p>
        </div>
      </main>
    </div>
  );
}
